<template>
  <div class="video-player-modal-overlay" @click="handleOverlayClick">
    <div class="video-player-modal" @click.stop>
      <img
        class="close-icon"
        :src="closeIcon"
        @click="$emit('close')"
        alt="close"
      />

      <div class="modal-content">
        <div class="video-section">
          <div class="video-header">
            <div class="video-title">{{ currentVideo.title }}</div>
            <div class="video-desc" v-if="currentVideo.desc">
              {{ currentVideo.desc }}
            </div>
          </div>

          <div class="video-player-wrap">
            <video
              ref="videoPlayer"
              :key="currentVideoIndex"
              :src="currentVideo.url"
              :poster="currentVideo.poster || currentVideo.url"
              autoplay
              controls
              x5-video-player-fullscreen="true"
              x5-video-player-type="h5"
              playsinline="true"
              webkit-playsinline="true"
            ></video>
          </div>
        </div>

        <div class="video-list-section" v-if="videoList.length > 1">
          <div class="video-list-title">
            {{ sectionTitle }}
          </div>
          <div class="video-list">
            <div
              v-for="(video, index) in videoList"
              :key="index"
              class="video-list-item"
              :class="{ active: index === currentVideoIndex }"
              @click="switchVideo(index)"
            >
              <div class="video-thumbnail">
                <img
                  v-if="video.poster"
                  :src="video.poster"
                  class="thumbnail-content"
                  alt="thumbnail"
                />
                <video
                  v-else
                  :src="video.url + '#t=0.1'"
                  class="thumbnail-content"
                  muted
                  preload="metadata"
                  playsinline
                ></video>
                <div class="play-overlay" v-if="index !== currentVideoIndex">
                  <InlineSvg
                    :src="
                      require('@/assets/images/keypal-two-instructions/play.svg')
                    "
                    class="play-icon"
                  />
                </div>
                <div class="playing-indicator" v-else>
                  <div class="playing-text">
                    {{ $t('keypalTwo.instructions.playing') }}
                  </div>
                </div>
              </div>
              <div class="video-item-title">{{ video.title }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import InlineSvg from '@/components/common/InlineSvg.vue';

export default {
  name: 'VideoPlayerModal',
  components: {
    InlineSvg,
  },
  props: {
    videoList: {
      type: Array,
      required: true,
      default: () => [],
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
    sectionTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentVideoIndex: this.initialIndex,
    };
  },
  computed: {
    ...mapGetters(['isDarkMode', 'isMobileSize']),
    currentVideo() {
      return this.videoList[this.currentVideoIndex] || {};
    },
    closeIcon() {
      if (this.isDarkMode) {
        return require('@/assets/images/keypal-two/close-dark.png');
      }
      return require('@/assets/images/keypal-two/close-1.png');
    },
  },
  watch: {
    currentVideoIndex() {
      this.$nextTick(() => {
        const video = this.$refs.videoPlayer;
        if (video) {
          video.play().catch(() => {
            // Handle autoplay failure
          });
        }
        // 切换视频时滚动到中央
        this.scrollVideoToCenter(this.currentVideoIndex);
      });
    },
  },
  mounted() {
    // Lock scroll when modal is open
    document.body.style.overflow = 'hidden';

    this.$nextTick(() => {
      const video = this.$refs.videoPlayer;
      if (video) {
        video.play().catch(() => {
          // Handle autoplay failure
        });
      }
      // 初始化时将当前视频滚动到中央
      this.scrollVideoToCenter(this.currentVideoIndex);
    });
  },
  beforeDestroy() {
    // Unlock scroll when modal is closed
    document.body.style.overflow = '';
  },
  methods: {
    handleOverlayClick() {
      this.$emit('close');
    },
    switchVideo(index) {
      if (index !== this.currentVideoIndex) {
        this.currentVideoIndex = index;
      }
    },
    scrollVideoToCenter(index) {
      this.$nextTick(() => {
        const videoListItems = this.$el.querySelectorAll('.video-list-item');
        const targetItem = videoListItems[index];
        const videoList = this.$el.querySelector('.video-list');

        if (!targetItem || !videoList) return;

        // 判断是横向还是纵向滚动
        const isHorizontal =
          videoList.scrollWidth > videoList.clientWidth &&
          videoList.scrollHeight <= videoList.clientHeight;

        if (isHorizontal) {
          // 横向滚动(移动端)
          const itemLeft = targetItem.offsetLeft;
          const itemWidth = targetItem.offsetWidth;
          const listWidth = videoList.offsetWidth;
          const targetScroll = itemLeft - (listWidth - itemWidth) / 2;

          videoList.scrollTo({
            left: targetScroll,
            behavior: 'smooth',
          });
        } else {
          // 纵向滚动(桌面端)
          const itemTop = targetItem.offsetTop;
          const itemHeight = targetItem.offsetHeight;
          const listHeight = videoList.offsetHeight;
          const targetScroll = itemTop - (listHeight - itemHeight) / 2;

          videoList.scrollTo({
            top: targetScroll,
            behavior: 'smooth',
          });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.video-player-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem;
}

.video-player-modal {
  position: relative;
  width: 100%;
  max-width: 14rem;
  background: var(--bg-2);
  border-radius: 0.32rem;
  padding: 0.4rem;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .close-icon {
    position: absolute;
    top: 0.16rem;
    right: 0.16rem;
    width: 0.4rem;
    height: 0.4rem;
    cursor: pointer;
    z-index: 10;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.1);
    }
  }

  .modal-content {
    display: flex;
    gap: 0.4rem;
    overflow: hidden;
    min-height: 0;
    flex: 1;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color-3);
      border-radius: 2px;

      &:hover {
        background: var(--color-4);
      }
    }
  }

  .video-section {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;

    .video-header {
      margin-bottom: 0.3rem;

      .video-title {
        font-size: 0.32rem;
        font-weight: 600;
        color: var(--color-1);
        line-height: 1.4;
        margin-bottom: 0.12rem;
      }

      .video-desc {
        font-size: 0.16rem;
        color: var(--color-3);
        line-height: 1.5;
      }
    }

    .video-player-wrap {
      width: auto;
      max-width: 100%;
      max-height: 100%;
      aspect-ratio: 16/9;
      border-radius: 0.16rem;
      overflow: hidden;
      background: #000;
      display: flex;
      align-items: center;
      justify-content: center;

      video {
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
      }
    }
  }

  .video-list-section {
    width: 3.2rem;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .video-list-title {
      font-size: 0.2rem;
      font-weight: 600;
      color: var(--color-1);
      margin-bottom: 0.2rem;
    }

    .video-list {
      display: flex;
      flex-direction: column;
      gap: 0.16rem;
      max-height: none;
      overflow-y: auto;
      padding-right: 0.08rem;
      flex: 1;

      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 2px;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--color-3);
        border-radius: 2px;

        &:hover {
          background: var(--color-4);
        }
      }

      &-item {
        cursor: pointer;
        padding: 0.12rem;
        background: var(--bg-10);
        border-radius: 0.12rem;
        border: 1px solid rgba(0, 0, 0, 0.1);
        transition: all 0.2s;

        &:hover {
          background: var(--bg-3);
        }

        &.active {
          background: rgba(41, 128, 254, 0.1);
          border: 1px solid #2980fe;
        }

        .video-thumbnail {
          position: relative;
          width: 100%;
          aspect-ratio: 16/9;
          max-height: 2rem;
          border-radius: 0.08rem;
          overflow: hidden;

          .thumbnail-content {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          .play-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.2s;

            .play-icon {
              width: 0.32rem;
              height: 0.32rem;
              color: #fff;
            }
          }

          .playing-indicator {
            position: absolute;
            bottom: 0.08rem;
            left: 0.08rem;
            background: rgba(0, 0, 0, 0.7);
            padding: 0.04rem 0.08rem;
            border-radius: 0.04rem;

            .playing-text {
              font-size: 0.12rem;
              color: #fff;
              font-weight: 600;
            }
          }
        }

        .video-item-title {
          margin-top: 0.08rem;
          font-size: 0.14rem;
          color: var(--color-4);
          line-height: 1.4;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      }
    }
  }
}

@media screen and (max-width: 1212px) {
  .video-player-modal {
    max-width: 95%;
    padding: 0.4rem;
    display: flex;
    flex-direction: column;

    .modal-content {
      flex-direction: column;
      overflow-y: auto;
      min-height: 0;
      padding-right: 0;
      display: flex;
    }

    .video-section {
      display: block;
      overflow: visible;

      .video-player-wrap {
        width: 100%;
        max-width: none;
        max-height: none;
        aspect-ratio: 16/9;
        margin: 0;
      }
    }

    .video-list-section {
      width: 100%;
      display: block;
      overflow: visible;

      .video-list {
        flex-direction: row;
        overflow-x: auto;
        overflow-y: hidden;
        max-height: none;
        padding-right: 0;
        padding-bottom: 0.08rem;
        flex: none;

        &::-webkit-scrollbar {
          height: 4px;
          width: auto;
        }

        &-item {
          min-width: 2.4rem;
          //flex-shrink: 0;
        }
      }
    }
  }
}

@media screen and (max-width: 767px) {
  .video-player-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .video-player-modal {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 0.32rem 0.32rem 0 0;
    padding: 0.4rem 0.32rem 0.6rem;

    .close-icon {
      top: 0.12rem;
      right: 0.12rem;
      width: 0.32rem;
      height: 0.32rem;
    }

    .modal-content {
      gap: 0.32rem;
    }

    .video-section {
      .video-header {
        margin-bottom: 0.24rem;

        .video-title {
          font-size: 0.28rem;
        }

        .video-desc {
          font-size: 0.14rem;
        }
      }
    }

    .video-list-section {
      .video-list-title {
        font-size: 0.18rem;
        margin-bottom: 0.16rem;
      }

      .video-list {
        gap: 0.12rem;

        &-item {
          min-width: 2rem;

          .video-item-title {
            font-size: 0.12rem;
          }
        }
      }
    }
  }
}
</style>
